import Icon from 'components/Icon';
import Pill from 'components/Widget/Pill';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { selectActiveDesignSystem } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import { shallowEqual } from 'react-redux';
import { DesignSystemSection } from 'designSystem/types';
import { DS_TAB_CHROME } from 'designSystem/config';
import { pathsToDsLinks } from 'designSystem/content/util';
import { addDsTab, setActiveDsTab } from 'redux/mode/actions';
import * as S from './styles';

interface DsUpdateCardProps {
	section: DesignSystemSection;
	paths?: string[];
	isMobile?: boolean;
	showPendingNote?: boolean;
}

const DsUpdateCard = ({ section, paths = [], isMobile, showPendingNote }: DsUpdateCardProps) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { designSystem, dsTabs } = useSelector(
		(r: RootReducer) => ({
			designSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			dsTabs: r.mode.dsTabs,
		}),
		shallowEqual
	);

	const chrome = DS_TAB_CHROME[section];
	const title = `${chrome.label} Updated`;
	const tabTargets = pathsToDsLinks(paths, section);

	const onItemClick = (e: React.MouseEvent, contentPath: string, label: string) => {
		e.stopPropagation();
		if (!designSystem) return;

		const tabId = `${section}-${contentPath}`;

		const existingTab = dsTabs.find((t) => t.id === tabId);
		if (existingTab) {
			dispatch(setActiveDsTab(tabId));
		} else {
			dispatch(
				addDsTab({
					id: tabId,
					section,
					contentPath,
					label,
					showBackButton: true,
				})
			);
		}

		const url = routes.designSystem(designSystem.id, designSystem.title);
		navigate(`${url}?section=${section}&contentPath=${encodeURIComponent(contentPath)}`);
	};

	return (
		<S.UpdateCard isMobile={isMobile}>
			<S.UpdateTitleRow>
				<S.UpdateIcon>
					<Icon type={chrome.icon} width={16} height={16} />
				</S.UpdateIcon>
				<S.UpdateTitle>{title}</S.UpdateTitle>
			</S.UpdateTitleRow>
			{tabTargets.length > 0 && (
				<S.ItemList>
					{tabTargets.map((target, i) =>
						target.deleted ? (
							<Pill
								key={`deleted-${target.label}-${i}`}
								label={target.label}
								variant="deleted"
								tooltip="Removed from design system"
							/>
						) : target.contentPath && !showPendingNote ? (
							<Pill
								key={target.contentPath}
								label={target.label}
								tooltip="Click to open in design system"
								onClick={(e) => onItemClick(e, target.contentPath!, target.label)}
							/>
						) : (
							<Pill key={target.contentPath ?? i} label={target.label} variant="muted" />
						)
					)}
				</S.ItemList>
			)}
			{showPendingNote && (
				<S.UpdateNote isMobile={isMobile}>Changes apply when chat completes</S.UpdateNote>
			)}
		</S.UpdateCard>
	);
};

export default DsUpdateCard;
